import * as React from "react"
import { Input } from "./input"
import { cn } from "@prescreen/lib/utils"

interface SSNInputProps extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value?: string
  onChange?: (value: string) => void
}

/**
 * Formats a social security number string to NNN-NN-NNNN format
 * @param value - The raw input value (may contain non-digits)
 * @returns Formatted SSN string
 */
function formatSSN(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')
  
  // Limit to 9 digits (US SSN)
  const limitedDigits = digits.slice(0, 9)
  
  // Format based on length
  if (limitedDigits.length === 0) {
    return ''
  } else if (limitedDigits.length <= 3) {
    return limitedDigits
  } else if (limitedDigits.length <= 5) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 5)}-${limitedDigits.slice(5)}`
  }
}

/**
 * Gets the cursor position after formatting
 * @param oldFormatted - Previous formatted value
 * @param newFormatted - New formatted value
 * @param oldCursorPos - Previous cursor position in the old formatted string
 * @returns New cursor position in the new formatted string
 */
function getCursorPosition(
  oldFormatted: string,
  newFormatted: string,
  oldCursorPos: number
): number {
  // Count digits before cursor in old value
  const oldDigitsBeforeCursor = oldFormatted
    .slice(0, Math.min(oldCursorPos, oldFormatted.length))
    .replace(/\D/g, '').length
  
  // Find position in new value where we have the same number of digits
  let newDigitCount = 0
  for (let i = 0; i < newFormatted.length; i++) {
    if (/\d/.test(newFormatted[i])) {
      newDigitCount++
      if (newDigitCount === oldDigitsBeforeCursor) {
        // Place cursor after this digit
        return i + 1
      }
    }
  }
  
  // If we haven't found it, place cursor at the end
  return newFormatted.length
}

/**
 * SSNInput component that automatically formats social security numbers as NNN-NN-NNNN
 * 
 * Best practices implemented:
 * - Formats as user types (onChange)
 * - Preserves cursor position when formatting
 * - Strips non-digits and formats
 * - Handles copy/paste correctly
 * - Allows partial input
 * - Uses controlled component pattern
 * - Limits input to 9 digits (US SSN format)
 */
const SSNInput = React.forwardRef<HTMLInputElement, SSNInputProps>(
  ({ className, value = '', onChange, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const previousValueRef = React.useRef<string>('')
    const previousCursorRef = React.useRef<number>(0)

    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    // Sync refs with value prop when it changes externally
    React.useEffect(() => {
      const formatted = formatSSN(value)
      if (formatted !== previousValueRef.current) {
        previousValueRef.current = formatted
      }
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const cursorPosition = e.target.selectionStart || 0
      
      // Get previous formatted value (from the input element itself, not the prop)
      const oldFormatted = inputRef.current?.value || previousValueRef.current || formatSSN(value)
      
      // Format the new SSN
      const formatted = formatSSN(inputValue)
      
      // Store current state for cursor positioning
      previousValueRef.current = oldFormatted
      previousCursorRef.current = cursorPosition
      
      // Call onChange with the formatted value
      onChange?.(formatted)
      
      // Calculate and set cursor position after React updates
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const newCursorPos = getCursorPosition(
            oldFormatted,
            formatted,
            cursorPosition
          )
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos)
          // Update refs for next change
          previousValueRef.current = formatted
          previousCursorRef.current = newCursorPos
        }
      })
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Store current cursor position before key press
      if (inputRef.current) {
        previousCursorRef.current = inputRef.current.selectionStart || 0
        previousValueRef.current = inputRef.current.value
      }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      // Store current state when input is focused
      previousCursorRef.current = e.target.selectionStart || 0
      previousValueRef.current = e.target.value
    }

    // Format the value prop
    const formattedValue = formatSSN(value)

    return (
      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={formattedValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder="XXX-XX-XXXX"
        maxLength={11} // NNN-NN-NNNN = 11 characters
        className={cn(className)}
        {...props}
      />
    )
  }
)

SSNInput.displayName = "SSNInput"

export { SSNInput }

